import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { submitContactForm } from '../mock';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    package: 'basic',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await submitContactForm(formData);
      setIsSubmitted(true);
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          business: '',
          package: 'basic',
          message: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      // Handle form submission error
      setFormData({ name: '', email: '', message: '' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="contact" className="py-32 bg-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-[600px] h-[600px] bg-brand-olive/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-[600px] h-[600px] bg-brand-yellow/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Ready to Grow Your Business?
            </h2>
            <p className="text-xl lg:text-2xl text-brand-cream/90 font-light">
              Book your free consultation today. Let's discuss how we can help you achieve your digital marketing goals.
            </p>
          </div>
          
          {/* Contact form */}
          <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl shadow-2xl p-12 md:p-16 border-2 border-brand-olive/40 backdrop-blur-xl">
            {isSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle className="w-20 h-20 text-brand-yellow mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
                <p className="text-brand-cream/80">We'll be in touch within 24 hours to schedule your free consultation.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white font-semibold">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="border-brand-olive/30 focus:border-brand-yellow focus:ring-brand-yellow bg-brand-brown/40 text-white placeholder:text-brand-cream/40"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white font-semibold">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="border-brand-olive/30 focus:border-brand-yellow focus:ring-brand-yellow bg-brand-brown/40 text-white placeholder:text-brand-cream/40"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white font-semibold">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+44 7700 900000"
                      className="border-brand-olive/30 focus:border-brand-yellow focus:ring-brand-yellow bg-brand-brown/40 text-white placeholder:text-brand-cream/40"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="business" className="text-white font-semibold">
                      Business Name
                    </Label>
                    <Input
                      id="business"
                      name="business"
                      type="text"
                      value={formData.business}
                      onChange={handleChange}
                      placeholder="Your Business Name"
                      className="border-brand-olive/30 focus:border-brand-yellow focus:ring-brand-yellow bg-brand-brown/40 text-white placeholder:text-brand-cream/40"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="package" className="text-white font-semibold">
                    Interested Package
                  </Label>
                  <select
                    id="package"
                    name="package"
                    value={formData.package}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-brand-olive/30 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-transparent bg-brand-brown/40 text-white"
                  >
                    <option value="basic">The Basic Package - £150/month</option>
                    <option value="advanced">The Advanced Package - £255/month</option>
                    <option value="custom">Custom Solution</option>
                    <option value="consultation">Just a Consultation</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white font-semibold">
                    Tell Us About Your Goals
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="What are you looking to achieve with your digital marketing?"
                    className="border-brand-olive/30 focus:border-brand-yellow focus:ring-brand-yellow resize-none bg-brand-brown/40 text-white placeholder:text-brand-cream/40"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-yellow hover:bg-brand-yellow/90 text-brand-brown font-semibold py-6 text-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Book Free Consultation
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
                
                <p className="text-sm text-brand-cream/60 text-center">
                  We respect your privacy. Your information will never be shared with third parties.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
